// Pure tenant-slug resolution from the current URL. Kept dependency-free so it
// can be called during app boot before Pinia or the router are initialized.
//
// Truth table (input → output):
//   event-flow.co                  → null
//   www.event-flow.co              → null
//   app.event-flow.co              → null   (dedicated app host, D2 domain cutover)
//   dev.event-flow.co              → null
//   localhost                      → null   (use ?tenant=... in dev)
//   127.0.0.1                      → null
//   <id>.amplifyapp.com            → null   (Amplify preview URL)
//   acme.event-flow.co             → 'acme'
//   acme.dev.event-flow.co         → 'acme'
//   acme.localtest                 → 'acme' (recommended hosts-file alias)
//   ?tenant=acme  (DEV only)       → 'acme' (escape hatch when not editing hosts)
//
// resolveTenantSlug only answers "what's the slug in the URL". The tenant
// store decides what to do with it: reserved slugs (RESERVED_SLUGS below)
// short-circuit to not_found without a lookup, everything else is resolved
// against organizations.subdomain.

// Slugs no organization may claim. Canonical list — the DB CHECK constraint
// in supabase/schema/tables.sql and the org-settings validator in
// useOrganizationConfig.js both mirror this set; keep all three in sync.
export const RESERVED_SLUGS = new Set([
  'www', 'dev', 'staging', 'prod', 'app', 'api', 'admin', 'auth',
  'mail', 'em', 'smtp', 'imap', 'ftp', 'cdn', 'static', 'assets',
  'docs', 'blog', 'help', 'support', 'status', 'billing', 'pay',
  'get', 'event-flow', 'eventflow', 'main',
]);

const APP_HOSTS = new Set([
  'event-flow.co',
  'www.event-flow.co',
  'app.event-flow.co',
  'dev.event-flow.co',
  'localhost',
  '127.0.0.1',
]);

export function resolveTenantSlug(hostname, search = '') {
  // DEV-only query-param override so subdomains can be tested without
  // editing the OS hosts file. Production builds ignore this.
  if (import.meta.env.DEV && search) {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const override = params.get('tenant');
    if (override) return normalize(override);
  }

  if (!hostname) return null;
  const host = hostname.toLowerCase();

  if (APP_HOSTS.has(host)) return null;

  // Amplify default URLs: <branch>.<appid>.amplifyapp.com — never tenant-routed.
  if (host.endsWith('.amplifyapp.com')) return null;

  const labels = host.split('.');
  if (labels.length < 2) return null;

  // *.event-flow.co  or  *.dev.event-flow.co
  if (host.endsWith('.event-flow.co')) {
    const prefixLabels = labels.slice(0, -2); // drop 'event-flow' and 'co'
    if (prefixLabels.length === 1) return normalize(prefixLabels[0]);
    if (prefixLabels.length === 2 && prefixLabels[1] === 'dev') return normalize(prefixLabels[0]);
    return null;
  }

  // *.localtest — recommended local hosts-file alias for dev.
  if (host.endsWith('.localtest')) {
    return normalize(labels[0]);
  }

  return null;
}

function normalize(slug) {
  const trimmed = String(slug || '').trim().toLowerCase();
  return trimmed || null;
}

// Convenience wrapper that reads from `window`. Safe to call in browser only.
export function getCurrentTenantSlug() {
  if (typeof window === 'undefined') return null;
  return resolveTenantSlug(window.location.hostname, window.location.search);
}
